import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinner from '@/components/LoadingSpinner';
import { fetchProdutosERP, produtoKeys } from '@/services/produtoService';
import { fetchHistoricoPublicacao, publicacaoKeys } from '@/services/publicacaoService';

export const Route = createFileRoute('/_authenticated/dashboard/')({
  component: Dashboard,
});

const PRODUTOS_PARAMS = { page: 1, page_size: 1 };
const HISTORICO_FILTROS = { page: 1, page_size: 5 };

function Dashboard() {
  const produtosQuery = useQuery({
    queryKey: produtoKeys.erp(PRODUTOS_PARAMS),
    queryFn: () => fetchProdutosERP(PRODUTOS_PARAMS),
  });
  const historicoQuery = useQuery({
    queryKey: publicacaoKeys.historico(HISTORICO_FILTROS),
    queryFn: () => fetchHistoricoPublicacao(HISTORICO_FILTROS),
  });

  const loading = produtosQuery.isLoading || historicoQuery.isLoading;
  const totalProdutos = produtosQuery.data?.total_count ?? 0;
  const recentActivity = historicoQuery.data?.items ?? [];

  const dashboardCards = [
    {
      title: 'Produtos Cadastrados',
      value: totalProdutos.toLocaleString('pt-BR'),
      link: '/cadastros-codigo-mapa',
      description: 'Total de produtos no sistema',
    },
    {
      title: 'Transações Disponíveis',
      value: '3 tipos',
      link: '/relatorio-transacoes',
      description: 'Produção, vendas e compras',
    },
    {
      title: 'Publicações recentes',
      value: String(recentActivity.length),
      link: '/historico-publicacao',
      description: 'Últimas entradas no histórico',
    },
    {
      title: 'Status do sistema',
      value: 'Online',
      link: '#',
      description: 'Funcionalidades ativas',
    },
  ];

  const quickActions = [
    {
      title: 'Ver transações',
      description: 'Relatório de transações agrupadas',
      link: '/relatorio-transacoes',
    },
    {
      title: 'Histórico de publicações',
      description: 'Consultar publicações MAPA',
      link: '/historico-publicacao',
    },
    {
      title: 'Gerenciar vínculos',
      description: 'Vínculos MAPA, cadastros e produtos sem vínculo',
      link: '/vinculos-mapa',
    },
  ];

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-semibold text-on-surface">Dashboard</h1>
          <p className="text-on-surface-variant">Visão geral do sistema MES</p>
        </div>
        <LoadingSpinner text="Carregando dashboard..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Dashboard</h1>
        <p className="text-on-surface-variant">Visão geral do sistema MES - Monitoramento e Estatísticas</p>
      </div>

      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-on-surface">Estatísticas do Sistema</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {dashboardCards.map((card, index) => (
            <Link
              key={index}
              to={card.link}
              className="no-underline"
            >
              <div className="h-full rounded-lg border border-outline-variant border-l-4 border-l-primary bg-surface-container-lowest p-4 transition-shadow hover:shadow-md">
                <h3 className="m-0 mb-1 font-semibold text-on-surface">{card.title}</h3>
                <div className="mb-1 text-2xl font-bold text-primary">{card.value}</div>
                <p className="m-0 text-sm text-on-surface-variant">{card.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-on-surface">Ações Rápidas</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.link}
              className="no-underline"
            >
              <div className="h-full rounded-lg border border-outline-variant border-l-4 border-l-primary bg-surface-container-lowest p-4 transition-shadow hover:shadow-md">
                <h3 className="m-0 mb-2 font-semibold text-on-surface">{action.title}</h3>
                <p className="m-0 leading-relaxed text-on-surface-variant">{action.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-on-surface">Atividades Recentes</h2>
        {recentActivity.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-outline-variant">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-outline-variant bg-surface-container text-left">
                  <th className="p-3">Tipo</th>
                  <th className="p-3">Mês</th>
                  <th className="p-3">Categoria</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Data</th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.map((item, index) => {
                  const ok = (item.status || '').toUpperCase() === 'SUCESSO' || item.sucesso === true;
                  const dataPub = item.data_publicacao || item.data_fim || item.data_inicio;
                  return (
                    <tr
                      key={item.id ?? index}
                      className="border-b border-outline-variant last:border-0"
                    >
                      <td className="p-3">
                        <span className="rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-on-primary">
                          {item.tipo_publicacao || '—'}
                        </span>
                      </td>
                      <td className="p-3">{item.mes ?? '—'}</td>
                      <td className="p-3">—</td>
                      <td className="p-3">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                            ok ? 'bg-success text-on-primary' : 'bg-error text-on-error'
                          }`}
                        >
                          {item.status || '—'}
                        </span>
                      </td>
                      <td className="p-3">{dataPub ? new Date(dataPub).toLocaleDateString('pt-BR') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg border border-outline-variant p-8 text-center">
            <p className="text-lg text-on-surface-variant">Nenhuma atividade recente</p>
            <p className="text-on-surface-variant">As publicações aparecerão aqui quando forem realizadas</p>
          </div>
        )}
      </section>
    </div>
  );
}
